/** Grid configuration */
export const GRID = {
    width: 60,
    height: 60,
    cellSize: 16,
};
/** Match configuration */
export const MATCH = {
    durationSeconds: 1800,
    tickRate: 10,
    shrinkStartSeconds: 1680,
    shrinkIntervalTicks: 20,
    shrinkCellsPerStep: 1,
    minPlayersToStart: 1,
    maxPlayers: 12,
    botFillCount: 7,
};
/** Snake configuration */
export const SNAKE_CONFIG = {
    initialLength: 3,
    coinsPerSegment: 5,
    baseMoveTickInterval: 2,
    maxLength: 15,
};
/** Coin configuration */
export const COINS = {
    initialSpawnCount: 80,
    maxOnMap: 120,
    respawnIntervalTicks: 15,
    respawnBatchSize: 3,
    commonValue: 1,
    rareValue: 3,
    jackpotValue: 10,
    rareChance: 0.15,
    jackpotChance: 0.03,
};
/** Death configuration */
export const DEATH = {
    /** 20-30% of coins scatter on the ground */
    dropPercentMin: 0.2,
    dropPercentMax: 0.3,
    /** Killer steals 15% of victim's coins directly */
    killerStealPercent: 0.15,
};
/** Extraction portal configuration */
export const EXTRACTION = {
    /** 3 portals appear together */
    portalCount: 3,
    /** Ticks before new portals spawn (30 seconds) */
    respawnDelayTicks: 300,
    /** Portals disappear after this many ticks (10 seconds) */
    portalDurationTicks: 100,
};
/** Chest thresholds */
export const CHESTS = {
    thresholds: [10, 25, 50, 100],
};
/** Bot configuration */
export const BOTS = {
    /** Ticks to wait before respawning a dead/extracted bot (10 seconds) */
    respawnDelayTicks: 100,
};
/** Coin chain / combo system */
export const CHAIN = {
    /** Moves after last coin pickup before chain breaks */
    windowMoves: 4,
    /** Extra moves if Streak Master perk is active */
    streakMasterBonus: 2,
    /** Multiplier step per chain level above 1 */
    multiplierStep: 0.5,
    /** Max chain level (multiplier caps here) */
    maxLevel: 5,
    /** Max multiplier value */
    maxMultiplier: 2.5,
};
/** Kill streak system */
export const KILL_STREAK = {
    /** Seconds between kills to maintain streak */
    windowSeconds: 8,
    /** Base bonus coins per streak tier */
    baseBonusCoins: 5,
    /** Max bonus coins cap */
    maxBonusCoins: 50,
};
/** Coin trail spawning (breadcrumb lines) */
export const COIN_TRAILS = {
    /** Chance that a respawn batch spawns a trail instead of individual coins */
    trailChance: 0.45,
    /** Min coins in a trail */
    minLength: 5,
    /** Max coins in a trail */
    maxLength: 10,
    /** Grid cells between each coin in the trail */
    spacing: 2,
    /** Whether the trail end coin is RARE tier */
    rareEndCoin: true,
};
/** Coin frenzy zone (temporary dense coin cluster) */
export const FRENZY = {
    /** Ticks between frenzy spawns (45 seconds at 10 tps) */
    intervalTicks: 450,
    /** First frenzy spawns after this many ticks (30 seconds) */
    firstSpawnTick: 300,
    /** Zone size range (cells) */
    zoneSizeMin: 5,
    zoneSizeMax: 6,
    /** Coins spawned in the zone */
    coinCount: 20,
    /** Duration in ticks (9 seconds) */
    durationTicks: 90,
};
/**
 * Stake multipliers — players opt into N× buyin at entry, scoring N×
 * lb_points so the arena pool naturally pays them out proportionally.
 * Whitelist enforced both client (UI buttons) and server (CHECK on
 * arena_participation.stake_multiplier + match_entries.stake_multiplier).
 *
 * Bots and x402 agents are pinned to 1 server-side regardless of input.
 */
export const STAKE_MULTIPLIERS = [1, 2, 5, 10];
export function isValidStakeMultiplier(n) {
    return typeof n === 'number' && STAKE_MULTIPLIERS.includes(n);
}
/** Derived constants */
export const MATCH_DURATION_TICKS = MATCH.durationSeconds * MATCH.tickRate;
export const SHRINK_START_TICK = MATCH.shrinkStartSeconds * MATCH.tickRate;
export const TICK_INTERVAL_MS = 1000 / MATCH.tickRate;
export const ARENA_CAPACITY_TABLE = [
    { gridSize: 60, maxHumans: 8 },
    { gridSize: 80, maxHumans: 14 },
    { gridSize: 100, maxHumans: 22 },
    { gridSize: 120, maxHumans: 32 },
];
/** Smallest configured grid that fits the given human count. Saturates
 *  to the largest entry if humanCount exceeds the table. Returns the
 *  first entry's gridSize for non-positive counts (single-player). */
export function pickGridSize(humanCount) {
    for (const entry of ARENA_CAPACITY_TABLE) {
        if (humanCount <= entry.maxHumans)
            return entry.gridSize;
    }
    return ARENA_CAPACITY_TABLE[ARENA_CAPACITY_TABLE.length - 1].gridSize;
}
/** Centered borderOffset that constricts a `gridSize` arena to the
 *  smallest table size that still fits `humanCount`. Useful when
 *  humans drop and we want the active zone to tighten without
 *  re-allocating the underlying grid. */
export function pickBorderOffset(humanCount, gridSize) {
    const desiredSide = pickGridSize(humanCount);
    if (desiredSide >= gridSize)
        return 0;
    return Math.max(0, Math.floor((gridSize - desiredSide) / 2));
}
//# sourceMappingURL=game-config.js.map